import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import cn from 'classnames';
import Mousetrap from 'mousetrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { userBans } from '@shopmy/universal';

import './BanUserModal.scss';

const { USER_BAN_CATEGORIES } = userBans;

const BanUserModal = ({ user, onConfirm, onClose }) => {
  const [category, setCategory] = useState(null);
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    Mousetrap.bind('esc', onClose);
    return () => Mousetrap.unbind('esc');
  }, [onClose]);

  const handleConfirm = async () => {
    if (!category) return cogoToast.warn('Pick a reason for the ban.');

    setIsSaving(true);
    const didBan = await onConfirm({ banCategory: category, banNote: note.trim() });
    if (!didBan) setIsSaving(false);
  };

  return (
    <div className='ban-user-modal-overlay' onClick={onClose}>
      <div className='ban-user-modal-panel' onClick={e => e.stopPropagation()}>
        <div className='panel-header'>
          <h2>Ban User</h2>
          <FontAwesomeIcon icon={faTimes} className='close-btn' onClick={onClose} />
        </div>

        <div className='panel-user-info'>
          <span className='user-name'>{user.name || 'Unknown'}</span>
          <span className='user-email'>{user.email}</span>
        </div>

        <div className='panel-body'>
          <div className='section-label'>Why is this user being banned?</div>
          <div className='category-list'>
            {USER_BAN_CATEGORIES.map(option => {
              const isActive = option.value === category;
              return (
                <div key={option.value} className={cn('category-card', { active: isActive })} onClick={() => setCategory(option.value)}>
                  <div className={cn('category-radio', { checked: isActive })} />
                  <span className='category-label'>{option.label}</span>
                </div>
              );
            })}
          </div>

          <div className='section-label'>Note (optional)</div>
          <textarea
            rows={3}
            placeholder='Anything the next person reviewing this account should know'
            value={note}
            onChange={({ target }) => setNote(target.value)}
          />

          <p className='ban-warning'>We will disable monetization on all of their pins and hide them from all brands.</p>

          <div className='panel-actions'>
            <button className='ban-btn' onClick={handleConfirm} disabled={isSaving}>
              {isSaving ? 'Banning...' : 'Ban User'}
            </button>
            <button className='cancel-btn' onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

BanUserModal.propTypes = {
  user: PropTypes.object.isRequired, // { id, name, email }
  onConfirm: PropTypes.func.isRequired, // ({ banCategory, banNote }) => Promise<boolean>, resolves true once the ban saved
  onClose: PropTypes.func.isRequired
};

export default BanUserModal;
